import classNames from 'classnames';
import React, { useState } from 'react';
import Alert from 'components/styleguide/alert/Alert';
import Icon from 'components/styleguide/icon/Icon';
import { LocationComparison, saveOverrides } from './utils/curationApi';
import { GateCheck, GateResult, effectiveGateVerdict } from './utils/curationScoring';
import { proficiencyLabel } from './utils/gateCheckPresenter';
import { useCurationReview } from './state/CurationReviewContext';
import ComparisonTable from './ComparisonTable';
import GateCheckRow from './GateCheckRow';
import GateDecision from './GateDecision';

const styles = require('./GateSection.module.scss');

const WORK_MODEL_LABELS: Record<string, string> = {
  hybrid: 'Hybrid',
  onsite: 'On-site',
  remote: 'Remote',
};

type GroupResult = 'pass' | 'fail' | 'na';

const groupResult = (checks: GateCheck[]): GroupResult => {
  const verdicts = checks.map(effectiveGateVerdict);
  if (verdicts.includes('fail')) return 'fail';
  if (verdicts.includes('na')) return 'na';
  return 'pass';
};

const GROUP_BANNERS: Record<string, Record<GroupResult, { detail: string; title: string }>> = {
  language: {
    fail: {
      detail: 'The candidate does not meet a required language.',
      title: 'Language check failed',
    },
    na: {
      detail: 'A required language is not on the profile. Review and set a result.',
      title: 'Language check needs review',
    },
    pass: {
      detail: 'Candidate meets all required language requirements.',
      title: 'Language check passed',
    },
  },
  location: {
    fail: {
      detail: 'The candidate does not meet a location requirement.',
      title: 'Location check failed',
    },
    na: {
      detail: 'A location check could not be verified. Review and set a result.',
      title: 'Location check needs review',
    },
    pass: {
      detail: 'All location requirements are met.',
      title: 'Location check passed',
    },
  },
};

const BANNER_ICONS: Record<GroupResult, string> = {
  fail: 'x',
  na: 'circleHelp',
  pass: 'check',
};

// Names where the candidate already sits inside the job's reach: their own
// city when it is the office city, otherwise their country when the server
// matched it against the eligible locations.
const basedInLabel = (location: LocationComparison): string | null => {
  const { candidate, job } = location;
  const officeCity = (job.officeLocations[0] || '').split(',')[0].trim().toLowerCase();
  if (candidate.city && officeCity && candidate.city.trim().toLowerCase() === officeCity) {
    return candidate.city;
  }
  return candidate.inEligibleLocation ? candidate.country : null;
};

function GroupBanner({ kind, result }: { kind: 'location' | 'language'; result: GroupResult }) {
  const copy = GROUP_BANNERS[kind][result];
  return (
    <div className={classNames(styles['ui-gate-banner'], styles[`ui-gate-banner--${result}`])}>
      <span className={styles['ui-gate-banner__title']}>
        <Icon aria-hidden name={BANNER_ICONS[result]} size="sm" />
        {copy.title}
      </span>
      <span className={styles['ui-gate-banner__detail']}>{copy.detail}</span>
    </div>
  );
}

// Both eligibility checks on one page: a job-vs-candidate comparison heads
// each group, every check row is overridable, and one decision closes the
// step.
export default function GateSection() {
  const { dispatch, initialReview, state, urls } = useCurationReview();
  const [saveError, setSaveError] = useState(false);

  const location = initialReview.comparison.location;
  const languages = initialReview.comparison.languages;
  const locationChecks = state.gateChecks.filter((check) => check.kind === 'location');
  const languageChecks = state.gateChecks.filter((check) => check.kind === 'language');

  const onOverride = async (key: string, value: GateResult | null) => {
    const previous = state.gateChecks.find((check) => check.key === key)?.override ?? null;
    dispatch({ key, type: 'OVERRIDE_GATE_CHECK', value });
    setSaveError(false);
    try {
      await saveOverrides(urls, { gate_overrides: { [key]: value } });
    } catch {
      // Restore what the curator had, not "no override": a failed save must
      // not silently discard an earlier decision.
      dispatch({ key, type: 'OVERRIDE_GATE_CHECK', value: previous });
      setSaveError(true);
    }
  };

  const attendance =
    location.job.officeAttendanceCount && location.job.officeAttendancePeriod
      ? ` (${location.job.officeAttendanceCount} days/${location.job.officeAttendancePeriod} on-site)`
      : '';

  const basedInMatch = basedInLabel(location);

  const utcOffsetLabel =
    location.candidate.utcOffset !== null
      ? ` (UTC${location.candidate.utcOffset >= 0 ? '+' : ''}${location.candidate.utcOffset})`
      : '';

  const jobLocationContent = (
    <dl>
      <div>
        <dt>Work model: </dt>
        <dd>
          {WORK_MODEL_LABELS[location.job.workModel] || 'Not specified'}
          {attendance}
        </dd>
      </div>
      {location.job.officeLocations.length > 0 && (
        <div>
          <dt>Office location: </dt>
          <dd>{location.job.officeLocations.join(' · ')}</dd>
        </div>
      )}
      {location.job.workModel === 'remote' && (
        <div>
          <dt>Open to anywhere: </dt>
          <dd>{location.job.openToWorldwide ? 'Yes' : 'No'}</dd>
        </div>
      )}
      {location.job.eligibleLocations.length > 0 && (
        <div>
          <dt>Eligible locations: </dt>
          <dd>{location.job.eligibleLocations.join(', ')}</dd>
        </div>
      )}
      <div>
        <dt>Regions excluded: </dt>
        <dd>{location.job.excludedLocations.join(', ') || 'None'}</dd>
      </div>
      <div>
        <dt>Timezone preference: </dt>
        <dd>{location.job.eligibleTimezones.join(' to ') || 'None'}</dd>
      </div>
      <div>
        <dt>International candidates: </dt>
        <dd>{location.job.citizenshipRequired ? 'Work permit required' : 'No restriction'}</dd>
      </div>
      <div>
        <dt>Visa support: </dt>
        <dd>{location.job.visaSupport ? 'Visa bureaucracy support available' : 'Not specified'}</dd>
      </div>
    </dl>
  );

  const candidateLocationContent = (
    <dl>
      <div className={styles['ui-gate-section__candidate-place']}>
        <Icon aria-hidden name="mapPin" size="sm" />
        <dd>
          {[location.candidate.city, location.candidate.country].filter(Boolean).join(', ') ||
            'Location unknown'}
        </dd>
      </div>
      {basedInMatch && (
        <div className={styles['ui-gate-section__candidate-match']}>
          <Icon aria-hidden name="check" size="sm" />
          <dd>Candidate is based in {basedInMatch}</dd>
        </div>
      )}
      <div>
        <dt>Citizenships: </dt>
        <dd>{location.candidate.citizenships.join(', ') || 'Unknown'}</dd>
      </div>
      <div>
        <dt>Timezone: </dt>
        <dd>
          {location.candidate.timezone ? `${location.candidate.timezone}${utcOffsetLabel}` : 'Unknown'}
        </dd>
      </div>
    </dl>
  );

  const jobLanguagesContent =
    languages.job.length > 0 ? (
      <ul className={styles['ui-gate-section__language-list']}>
        {languages.job.map((language) => (
          <li key={language.code}>{language.name} · required</li>
        ))}
      </ul>
    ) : (
      <span>No required languages.</span>
    );

  const candidateLanguagesContent =
    languages.candidate.length > 0 ? (
      <ul className={styles['ui-gate-section__language-list']}>
        {languages.candidate.map((language) => (
          <li key={language.code}>
            {language.name} · {proficiencyLabel(language.level)}
          </li>
        ))}
      </ul>
    ) : (
      <span>No languages on the profile.</span>
    );

  return (
    <section aria-label="Eligibility checks" className={styles['ui-gate-section']}>
      <h2 className={styles['ui-gate-section__heading']}>Eligibility checks</h2>
      {saveError && (
        <Alert message="The override couldn't be saved. Try again." variant="error" />
      )}

      <div className={styles['ui-gate-section__group']}>
        <ComparisonTable
          candidateContent={candidateLocationContent}
          candidateTitle="Candidate location"
          jobContent={jobLocationContent}
          jobTitle="Job location"
        />
        <ul className={styles['ui-gate-section__checks']}>
          {locationChecks.map((check) => (
            <GateCheckRow
              check={check}
              disabled={state.submitted}
              key={check.key}
              onOverride={onOverride}
            />
          ))}
        </ul>
        {locationChecks.length > 0 && (
          <GroupBanner kind="location" result={groupResult(locationChecks)} />
        )}
      </div>

      <div className={styles['ui-gate-section__group']}>
        <ComparisonTable
          candidateContent={candidateLanguagesContent}
          candidateTitle="Candidate languages"
          jobContent={jobLanguagesContent}
          jobTitle="Job language requirements"
        />
        <ul className={styles['ui-gate-section__checks']}>
          {languageChecks.map((check) => (
            <GateCheckRow
              check={check}
              disabled={state.submitted}
              key={check.key}
              onOverride={onOverride}
            />
          ))}
        </ul>
        {languageChecks.length > 0 && (
          <GroupBanner kind="language" result={groupResult(languageChecks)} />
        )}
      </div>

      <GateDecision />
    </section>
  );
}
